import moment from 'moment'

/**
 * Staff accounts that are automation, not people: they are classified under
 * the System role (see `roleFor` below) alongside rows that have no actor at
 * all. Matched by username, never by id, because the same service account
 * has a different pk in every environment. Add further service accounts
 * here; nothing else needs to change.
 *
 * Defined here rather than in `reportRegistry.js` (which is where the
 * interface conceptually belongs, and which re-exports this for callers that
 * want it from there): `reportRegistry.js` imports `PALETTE`/`entityLabel`
 * from this module, and `PALETTE` is read at that module's top level
 * (`DIRECTION_SERIES`), so an import running the other way round would be a
 * circular import that breaks at module-init -- `reportRegistry.js` would
 * start evaluating, hit its own `import ... from './activityReport'` before
 * this module has finished (because this module's own top-level import of
 * `reportRegistry.js` is still in progress), and read `PALETTE` as
 * `undefined`.
 */
export const SYSTEM_USERNAMES = ['cloud_functions']

// Categorical palette: the validated 8-slot order. Verified against the #ffffff
// chart surface -- lightness band, chroma floor, CVD separation and normal-vision
// floor all pass. Three slots (aqua, yellow, magenta) sit below 3:1 contrast, so
// the relief rule applies: every chart using them ships a table view.
//
// Slots are assigned by entity identity and never cycled. A 9th series folds into
// OTHER rather than generating a new hue, which would be indistinguishable under
// colour-vision deficiency.
export const PALETTE = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948']
export const OTHER_COLOR = '#898781'

// Reserved series key for the overflow bucket in `buildScopeTrend`. A private
// token rather than the label 'Other' so a board literally named "Other" can
// never collide with it.
export const OTHER_KEY = '__other__'

/**
 * Normalise a group-key result to a list. `keyFn` may return a scalar (the
 * per-user builders: `row.userName`) or an array (`row.scopeNames`, which can
 * carry several inboxes for one message). Null/undefined values are dropped,
 * so a row with no value in this dimension simply does not count -- the same
 * rule `buildActionMix` applies to its `groupKeyFn`.
 */
export const groupValues = (row, keyFn) => {
    const value = keyFn(row)
    const list = Array.isArray(value) ? value : [value]
    return list.filter((key) => key !== null && key !== undefined)
}

const DEFAULT_SERIES_KEY = (row) => row.action

/**
 * Sentinel exclude-user "id" standing in for "no actor at all" (`userId ===
 * null`, labelled "No Actor" -- see the three-population docstring in
 * `activity/query.py`). Not a real `auth_user` pk -- a row with no actor has
 * no id to exclude by, so this constant is the value the No Actor option (in
 * `useActivityReport`'s `excludeUserOptions`) and `filterRows` below agree to
 * treat as that row's "id". Never collides with a real id: ids arriving over
 * the wire are numeric, this is not.
 */
export const SYSTEM_EXCLUDE_VALUE = 'system'

export const ROLE_OPTIONS = [
    { value: 'rep', label: 'Reps' },
    { value: 'customer', label: 'Customers' },
    { value: 'system', label: 'System' },
]

/**
 * Which of the three actor populations a row belongs to. Every row falls
 * into exactly one -- see the three-population docstring in
 * activity/query.py for where the populations come from:
 *   system   -- no actor at all (`userId` null), or a listed service account
 *   customer -- an id that matched no staff account (a mobile driver)
 *   rep      -- everyone else
 * Evaluated on the raw row, before `useActivityReport` fills in display
 * names, so it keys off `!row.userName` -- true for both `null` and `''`,
 * either of which means "no match" -- not a strict `userName === null`
 * check.
 */
export const roleFor = (row) => {
    if (row.userId === null || SYSTEM_USERNAMES.includes(row.username)) return 'system'
    if (!row.userName) return 'customer'
    return 'rep'
}

/**
 * The count a row contributes, picking the column that matches the action's
 * semantics: distinct objects for creations, history events for updates.
 *
 * `actionMeta` is passed in rather than read from a module constant, because
 * this module is now shared by ten reports whose actions differ. The registry
 * owns the definitions; this file owns the maths.
 */
export const rowCount = (row, actionMeta) => {
    const meta = actionMeta(row.action)
    if (!meta) return 0
    return Number(row[meta.metric]) || 0
}

/**
 * How many distinct records the given rows *updated*.
 *
 * A union rather than a sum, and the whole reason rows carry ids. `objectsCount`
 * is computed per day, so summing it double-counts anything touched on more than
 * one day; unioning ids collapses those days into one record. The union also
 * spans users, so a record two people edited counts once.
 *
 * Creation rows are skipped: every row carries ids, so without this filter the
 * answer silently becomes "records created or updated". Creations need no union
 * anyway -- an object is created once, so it cannot span days.
 *
 * Keyed by entity as well as id, because `objectId` is only unique within a
 * record type: incident 1 and agreement 1 are different records.
 */
export const countUniqueUpdatedObjects = (rows, actionMeta) => {
    const seen = new Set()
    rows.forEach((row) => {
        const meta = actionMeta(row.action)
        if (meta?.mode !== 'updated') return
        ;(row.objectIds || []).forEach((objectId) => seen.add(`${meta.entity}|${objectId}`))
    })
    return seen.size
}

/**
 * Narrow rows by any combination of role, action, branch, user, user id and
 * excluded user id.
 *
 * An empty list for a dimension means "no filter on it". Dimensions intersect,
 * so selecting a group and a user shows only that user, and only if they are in
 * the group.
 *
 * `roles` narrows by `row.role` (see `roleFor` above -- 'rep', 'customer' or
 * 'system'), stamped onto every row by `useActivityReport` before filtering
 * ever runs. Checked first, like every other dimension here, it simply has
 * nothing to intersect against until a caller passes it.
 *
 * `userIds` is separate from `users` on purpose: the group filter resolves to
 * user ids, because display names are not unique and the report's name formula
 * does not match `UserNode.resolve_name` in every case. Ids are compared as
 * strings so a numeric row id and a GraphQL `pk` line up.
 *
 * `excludeUserIds` follows the same id-based convention as `userIds`, for the
 * same reason -- and for the same reason it is not `excludeUsers` matched by
 * name. It is a separate pass rather than folded into `userIds`: intersecting
 * (AND) is the right relationship between "must be one of these" and "must not
 * be one of these", and it is what makes exclusion win when a user appears in
 * both -- a row for that user still fails the exclude check even though it
 * passed the include check, so it drops out either way. A row with no actor at
 * all (`userId === null`, "No Actor") has no real id to compare, so it is
 * matched via `SYSTEM_EXCLUDE_VALUE`, the sentinel the exclude-users No
 * Actor option uses in place of an id.
 *
 * `scope` narrows by `row.scopeNames` -- a report's one optional additional
 * grouping dimension (Board for `boards`, Inbox for `inbox`; see
 * `scopeLabelFor` in reportRegistry.js and `ActivityReportRowNode.scope_names`
 * in activity/schema.py). `scopeNames` is a *list* -- an object can resolve
 * to more than one value (a message routed into several inboxes) -- so a row
 * matches when ANY of its values is one of the selected ones, not when the
 * whole list equals a single selected value. `[]` for every row on a report
 * that declares no dimension, which is harmless here: those reports never
 * offer this filter, so `scope` is always empty for them, and an empty
 * `scopeNames` never matches a non-empty filter either way.
 */
export const filterRows = (
    rows,
    {
        roles = [],
        actions = [],
        branches = [],
        scope = [],
        users = [],
        userIds = [],
        excludeUserIds = [],
    } = {}
) => {
    const roleSet = roles.length ? new Set(roles) : null
    const actionSet = actions.length ? new Set(actions) : null
    const branchSet = branches.length ? new Set(branches) : null
    const scopeSet = scope.length ? new Set(scope) : null
    const userSet = users.length ? new Set(users) : null
    const userIdSet = userIds.length ? new Set(userIds.map(String)) : null
    const excludeUserIdSet = excludeUserIds.length ? new Set(excludeUserIds.map(String)) : null

    return rows.filter((row) => {
        if (roleSet && !roleSet.has(row.role)) return false
        if (actionSet && !actionSet.has(row.action)) return false
        if (branchSet && !branchSet.has(row.branchName)) return false
        if (scopeSet && !(row.scopeNames || []).some((scopeName) => scopeSet.has(scopeName))) return false
        if (userSet && !userSet.has(row.userName)) return false
        if (userIdSet && !userIdSet.has(String(row.userId))) return false
        if (excludeUserIdSet) {
            const comparableId = row.userId === null ? SYSTEM_EXCLUDE_VALUE : String(row.userId)
            if (excludeUserIdSet.has(comparableId)) return false
        }
        return true
    })
}

/** Sum `rowCount` into a plain object keyed by `keyFn`. */
export const totalsBy = (rows, keyFn, actionMeta) =>
    rows.reduce((acc, row) => {
        const key = keyFn(row)
        acc[key] = (acc[key] || 0) + rowCount(row, actionMeta)
        return acc
    }, {})

/**
 * Like `totalsBy`, but `keyFn` returns a *list* of keys per row, and the
 * row's count is added to every one of them -- the same "double counting"
 * `buildActionMix` does for a list-valued `groupKeyFn` (see its own
 * docstring). Used for a list-valued dimension's own filter option list
 * (`scopeOptions` in useActivityReport.js): ranking has to see every value a
 * row carries, not just the first, or a message routed to Sales and Support
 * but never on its own to Billing would still leave Billing rankable only by
 * whatever volume it happens to get from other, single-routed messages --
 * this is what makes every value a row actually carries count towards its
 * own option.
 */
export const totalsByMulti = (rows, keyFn, actionMeta) =>
    rows.reduce((acc, row) => {
        const keys = keyFn(row) || []
        keys.forEach((key) => {
            if (key === null || key === undefined) return
            acc[key] = (acc[key] || 0) + rowCount(row, actionMeta)
        })
        return acc
    }, {})

/** Keys of a totals object, largest first. */
export const rankKeys = (totals) => Object.keys(totals).sort((a, b) => totals[b] - totals[a])

export const toOptions = (values) => values.map((value) => ({ label: value, value }))

export const daysBetween = (startDate, endDate) => {
    const days = []
    const cursor = moment(startDate)
    const last = moment(endDate)
    while (cursor.isSameOrBefore(last, 'day')) {
        days.push(cursor.format('YYYY-MM-DD'))
        cursor.add(1, 'day')
        if (days.length >= 366) break
    }
    return days
}

/** The three time axis buckets the report toolbar's granularity switch offers. */
export const GRANULARITIES = ['day', 'week', 'month']

/** Noun/adjective labels for a granularity, used in chart titles and captions
 *  (e.g. "Busiest {period}", "{Period} activity trend"). */
export const periodLabel = (granularity) => ({ day: 'day', week: 'week', month: 'month' })[granularity]
export const periodAdjective = (granularity) => ({ day: 'Daily', week: 'Weekly', month: 'Monthly' })[granularity]

/**
 * The bucket a row's `eventDate` falls into for a given granularity.
 *
 * `day` is the date itself, unchanged. `week` is Monday-start ISO: the
 * Monday of the ISO week `eventDate` falls in, via `isoWeekday(1)` -- this
 * walks backward or forward within the same ISO week to land on Monday, so
 * a Sunday belongs to the week that started the Monday before it, not the
 * Monday after. `month` is the calendar month.
 */
export const periodKeyFor = (eventDate, granularity = 'day') => {
    const date = moment(eventDate)
    if (granularity === 'week') return date.isoWeekday(1).format('YYYY-MM-DD')
    if (granularity === 'month') return date.format('YYYY-MM')
    return date.format('YYYY-MM-DD')
}

/** A week bucket's label: 'MMM D – D' within one month, 'MMM D – MMM D' across a
 *  month boundary -- `start`/`end` are already the clamped bucket edges. */
const weekLabel = (start, end) => {
    const startMoment = moment(start)
    const endMoment = moment(end)
    const startLabel = startMoment.format('MMM D')
    if (startMoment.isSame(endMoment, 'month')) return `${startLabel} – ${endMoment.format('D')}`
    return `${startLabel} – ${endMoment.format('MMM D')}`
}

/**
 * Ordered time buckets covering `[startDate, endDate]`, one per granularity
 * step: `{ key, label, start, end }`. `start`/`end` are `'YYYY-MM-DD'`,
 * clamped to the requested range, so a week or month bucket at either edge
 * of the range never claims days outside it.
 *
 * Built by walking `daysBetween`'s day list rather than stepping the
 * calendar independently, so this inherits `daysBetween`'s 366-day cap for
 * free and the clamped start/end fall out automatically -- a partial first
 * or last bucket is exactly the run of days `daysBetween` actually produced.
 * Day mode returns those same keys directly (`daysBetween(startDate,
 * endDate)`), so it is byte-identical to the pre-granularity behaviour.
 */
export const periodsBetween = (startDate, endDate, granularity = 'day') => {
    const days = daysBetween(startDate, endDate)

    if (granularity === 'day') {
        return days.map((day) => ({ key: day, label: moment(day).format('MMM D'), start: day, end: day }))
    }

    const buckets = []
    days.forEach((day) => {
        const key = periodKeyFor(day, granularity)
        const current = buckets[buckets.length - 1]
        if (current && current.key === key) {
            current.end = day
        } else {
            buckets.push({ key, start: day, end: day })
        }
    })

    return buckets.map(({ key, start, end }) => ({
        key,
        start,
        end,
        label: granularity === 'week' ? weekLabel(start, end) : moment(key, 'YYYY-MM').format('MMM YYYY'),
    }))
}

/**
 * Group x day grid for the heatmap. Days with no activity are still emitted so
 * gaps in someone's week are visible rather than collapsed away.
 *
 * `groupKeyFn`/`groupKey` follow the same convention as `buildLeaderboard`
 * below: default to `row.userName` (unchanged behaviour), or take a
 * list-valued key (e.g. `row.scopeNames`) via `groupValues`, fanning a row's
 * count out to every group it names.
 */
export const buildHeatmap = (
    rows,
    startDate,
    endDate,
    actionMeta,
    { groupKeyFn = (row) => row.userName, groupKey = 'userName', granularity = 'day' } = {}
) => {
    const periods = periodsBetween(startDate, endDate, granularity)
    const days = periods.map((period) => period.key)

    const totals = totalsByMulti(
        rows,
        (row) => groupValues(row, groupKeyFn).map((key) => `${key}|${periodKeyFor(row.eventDate, granularity)}`),
        actionMeta
    )
    const groupTotals = totalsByMulti(rows, (row) => groupValues(row, groupKeyFn), actionMeta)
    const groups = rankKeys(groupTotals)
    const peak = Math.max(0, ...Object.values(totals))

    const grid = groups.map((key) => ({
        [groupKey]: key,
        total: groupTotals[key],
        cells: days.map((day) => ({ day, count: totals[`${key}|${day}`] || 0 })),
    }))

    return { periods, days, grid, peak }
}

/**
 * Bucket a count onto a 0-6 step of the sequential blue ramp. Returned as a level
 * rather than a colour so the cell can wear a class instead of an inline style.
 */
export const heatLevel = (count, peak) => {
    if (!count) return 0
    if (peak <= 0) return 0
    return Math.max(1, Math.min(6, Math.ceil((count / peak) * 6)))
}

/**
 * Group rows by anything (user, branch, or a list-valued dimension) with one
 * numeric field per series, sorted by total descending.
 *
 * Records are keyed by `seriesKeyFn`, which defaults to the action name itself
 * so a chart series key *is* an action -- no second naming scheme to keep in
 * sync. The User Activity report overrides it to collapse thirty-odd actions
 * onto created/edited, which would otherwise be an unreadable legend.
 *
 * `groupKeyFn(row)` normally returns one scalar (a branch or user name) and
 * the row's count lands in exactly one group -- unchanged from before. It may
 * instead return an *array* (the by-scope chart's `row.scopeNames`, e.g. an
 * inbox report's message routed into three inboxes), in which case the row's
 * count is added to *every* group named in that array -- one message routed
 * to three inboxes contributes to all three bars. This is deliberate double
 * counting, scoped to exactly this call site by what `groupKeyFn` happens to
 * return for a given chart's rows: every other caller's `groupKeyFn` (by
 * user, by branch) still returns a scalar, so it still counts each row once,
 * with nothing here that has to know which dimension it is looking at to
 * make that true.
 *
 * `seriesKeys`, when passed, is the closed set of series a caller's chart
 * actually renders one `<Bar>` (or column) for -- e.g. `['inbound',
 * 'outbound']` for a `colorBy: 'direction'` chart. It exists because
 * `seriesKeyFn` and `wanted` (above) can disagree about what a "valid" key
 * is: the default `seriesKeyFn` returns `row.action`, and `wanted` is built
 * from that same `actions` list, so the two are always in lockstep and every
 * key this function ever produces is one `wanted` already approved. A
 * `seriesKeyFn` keyed off a *different* field -- direction, not action --
 * breaks that lockstep: `wanted` still only screens `row.action`, so a value
 * that field can legitimately take (the backend's `direction` column is
 * `COALESCE(map_direction.direction, 'unknown')`, so a row can carry the
 * literal string `'unknown'` when its message was deleted after its history
 * row was written) sails through as a truthy `seriesKey` with nowhere to
 * render. Left uncaught, that row's count still landed in `total` via the
 * unconditional `total +=` below, while no segment showed it -- the bar's
 * printed total then exceeded its visible stacked length by an unexplained
 * amount. `buildDirectionTotals` (the sibling stat tile) already treats an
 * unresolvable direction as counting toward neither total; passing
 * `seriesKeys` here is what makes this chart agree with that same choice,
 * generally -- by checking membership in the chart's actual series, not by
 * special-casing the string `'unknown'`. Omitted (as every action-keyed
 * chart omits it), this is a no-op: `wanted` alone already guarantees every
 * key is renderable, so there is nothing left to filter.
 */
export const buildActionMix = (
    rows,
    { groupKeyFn, actions, groupKey = 'name', actionMeta, seriesKeyFn = DEFAULT_SERIES_KEY, seriesKeys }
) => {
    const wanted = new Set(actions)
    const knownSeries = seriesKeys ? new Set(seriesKeys) : null
    const byGroup = {}

    rows.forEach((row) => {
        if (!wanted.has(row.action)) return
        const meta = actionMeta(row.action)
        const seriesKey = seriesKeyFn(row, meta)
        if (!seriesKey) return
        // A key with no rendered series to land in must not touch `total`
        // either -- see the docstring above.
        if (knownSeries && !knownSeries.has(seriesKey)) return

        const groupValue = groupKeyFn(row)
        const keys = Array.isArray(groupValue) ? groupValue : [groupValue]
        keys.forEach((key) => {
            if (key === null || key === undefined) return
            if (!byGroup[key]) byGroup[key] = { [groupKey]: key, total: 0 }
            byGroup[key][seriesKey] = (byGroup[key][seriesKey] || 0) + rowCount(row, actionMeta)
            byGroup[key].total += rowCount(row, actionMeta)
        })
    })

    return Object.values(byGroup).sort((a, b) => b.total - a.total)
}

/** One record per period with a field per series key, gaps included. */
export const buildActionTrend = (
    rows,
    startDate,
    endDate,
    actions,
    actionMeta,
    seriesKeyFn = DEFAULT_SERIES_KEY,
    granularity = 'day'
) => {
    const periods = periodsBetween(startDate, endDate, granularity)
    const wanted = new Set(actions)
    const scoped = rows.filter((row) => wanted.has(row.action))

    // Series keys come from the actions in scope, not the rows that happened to
    // survive filtering -- an action with zero matching rows in this range still
    // gets its field on every record, defaulting to 0. Deriving keys from `scoped`
    // instead would drop that field to `undefined`, which a chart renders as a
    // gap (or throws on) rather than a flat zero line.
    const seriesKeys = new Set()
    actions.forEach((action) => {
        const seriesKey = seriesKeyFn({ action }, actionMeta(action))
        if (seriesKey) seriesKeys.add(seriesKey)
    })

    const totals = {}
    scoped.forEach((row) => {
        const seriesKey = seriesKeyFn(row, actionMeta(row.action))
        if (!seriesKey) return
        const key = `${periodKeyFor(row.eventDate, granularity)}|${seriesKey}`
        totals[key] = (totals[key] || 0) + rowCount(row, actionMeta)
    })

    // `day` is kept alongside `period` -- identical to it in day mode, so any
    // existing reader of `record.day` (e.g. a chart's `dataKey`) keeps working
    // unchanged until it is migrated to `period`.
    return periods.map(({ key, label }) => {
        const record = { period: key, label, day: key }
        seriesKeys.forEach((seriesKey) => {
            record[seriesKey] = totals[`${key}|${seriesKey}`] || 0
        })
        return record
    })
}

/** Rows -> DataTable rows, given series whose keys index the record. */
export const buildMixTableRows = (data, series, groupKey = 'name') =>
    data.map((record) => ({
        name: record[groupKey],
        total: record.total || 0,
        values: series.reduce((acc, item) => ({ ...acc, [item.key]: record[item.key] || 0 }), {}),
    }))

/**
 * The figures a report's stat row shows. Generic on purpose -- these are the
 * only counts that mean the same thing on all ten reports.
 *
 * `headlineActions`, when given, scopes "created" and "edited" down to a
 * subset of the report's actions -- e.g. `incidents` excludes its party and
 * document actions, so "Incidents created" counts incidents, not documents
 * (see `headlineActionsFor` in reportRegistry.js). It is filtered in *before*
 * `countUniqueUpdatedObjects` runs its union, not applied to the union's
 * result -- summing per-day/per-user counts after the fact would reintroduce
 * exactly the double-counting that function exists to avoid. Omitting
 * `headlineActions` scopes to every action in the report, which is the
 * eight single-record-type reports' default.
 *
 * "Total edit events" (`editEvents`) is deliberately *not* scoped by
 * `headlineActions` -- it is the raw update-event count across every action
 * in the report (i.e. every action `rows` carries), so it stays larger than
 * `edited` whenever a report has non-headline actions with their own update
 * events (documents, parties, insurance removals).
 */
export const buildKpis = (rows, startDate, endDate, actionMeta, headlineActions) => {
    const headlineSet = headlineActions ? new Set(headlineActions) : null
    const headlineRows = headlineSet ? rows.filter((row) => headlineSet.has(row.action)) : rows

    const created = headlineRows.filter((row) => actionMeta(row.action)?.mode === 'created')
    const headlineUpdated = headlineRows.filter((row) => actionMeta(row.action)?.mode === 'updated')
    const allUpdated = rows.filter((row) => actionMeta(row.action)?.mode === 'updated')

    return {
        created: created.reduce((total, row) => total + rowCount(row, actionMeta), 0),
        // Distinct records edited, scoped to the headline actions -- unions
        // ids across days and users (see `countUniqueUpdatedObjects`), so a
        // record two people edited on two different days still counts once.
        edited: countUniqueUpdatedObjects(headlineUpdated, actionMeta),
        // The raw event total, unscoped -- every update event this report
        // logs, across every record type it tracks.
        editEvents: allUpdated.reduce((total, row) => total + rowCount(row, actionMeta), 0),
        // Distinct actors among the rows this report was handed -- by id, not
        // name (see filterRows above), so two people sharing a name still
        // count as two. No null special-case here: which populations reach
        // `rows` at all is decided upstream, by the Role filter (`roleFor` /
        // `ROLE_OPTIONS`, defaulting to Reps only) intersected with everything
        // else on the toolbar -- not by this function guessing who counts as
        // "staff". A null userId ("No Actor") counts as one distinct actor
        // exactly like any other id, the moment the filters admit it (e.g.
        // the System role is selected).
        activeUsers: new Set(rows.map((row) => row.userId)).size,
    }
}

/**
 * Value for a report-declared "extra" stat tile (see `report.extraTiles` in
 * reportRegistry.js -- e.g. incidents' "Documents uploaded"). Sums `rowCount`
 * over just the tile's own `actions`, the same per-row metric selection every
 * other tile already uses: a created-mode action reads `objectsCount`, an
 * updated-mode one reads `eventsCount` (see `rowCount`). For a tile scoped to
 * a single created-mode action -- the only kind so far -- that sum is already
 * a distinct-object count with no union needed, exactly like "{noun} created"
 * above: an object is created once, so `objectsCount` can't double-count it
 * across days the way an update event can.
 */
export const buildExtraTileValue = (rows, actionMeta, actions) => {
    const wanted = new Set(actions)
    return rows
        .filter((row) => wanted.has(row.action))
        .reduce((total, row) => total + rowCount(row, actionMeta), 0)
}

/**
 * Distinct inbound and outbound messages *created* in `rows`.
 *
 * Distinct objects, never events, and the reason this cannot be a `rowCount`
 * sum like every other tile: an outbound message moves through QUEUED ->
 * SENT/DELIVERED -> FAILED, each a separate history event, and `objectsCount`
 * is computed per day, so a message touched on two days appears twice.
 * Unioning ids collapses both -- the same argument as
 * `countUniqueUpdatedObjects`, applied to creations as well as updates.
 *
 * Scoped by `mode === 'created'` as well as `entity`, on purpose: without the
 * mode check, an updated-mode row (a delivery-status webhook flipping
 * QUEUED -> SENT -> DELIVERED, attributed to System) counts as a message
 * "touched" in range even when the message itself was created long before
 * the range started, or was never created by anyone this report's other
 * tiles would count. That is a different question from "messages created in
 * this range" -- the one this tile's headline pairing (see
 * `buildKpis`/ReportBody.js) needs it to answer, since `in + out` is meant to
 * sum to exactly the same "created" count and cannot if updated-mode rows
 * are allowed to add ids of their own.
 *
 * Scoped to one entity, so attachments -- which inherit their parent's
 * direction in `DIRECTION_SOURCES`, and so do carry one -- stay out of a tile
 * whose label says "messages".
 *
 * Rows whose direction did not resolve (the `unknown` fallback: a message
 * deleted from MySQL after its history was written) count toward neither
 * total rather than being forced into one.
 */
export const buildDirectionTotals = (rows, actionMeta, entity) => {
    const seen = { inbound: new Set(), outbound: new Set() }

    rows.forEach((row) => {
        const bucket = seen[row.direction]
        if (!bucket) return
        const meta = actionMeta(row.action)
        if (meta?.entity !== entity || meta?.mode !== 'created') return
        ;(row.objectIds || []).forEach((objectId) => bucket.add(objectId))
    })

    return { inbound: seen.inbound.size, outbound: seen.outbound.size }
}

/**
 * Map a palette hex back to its slot number so DOM elements (legend and tooltip
 * swatches) can wear a `viz-swatch--n` class instead of an inline background.
 *
 * Recharts hands us the resolved fill colour in its tooltip payload, and every
 * colour we ever pass in comes from PALETTE or OTHER_COLOR, so this is total.
 */
export const slotForColor = (color) => {
    const index = PALETTE.indexOf(color)
    return index === -1 ? 'other' : index + 1
}

/* -------------------------------------------------------------------------
   Users view
   -------------------------------------------------------------------------
   Every report's second chart layout: who did the work, not what it was on.
   Generic on purpose, exactly like the maths above -- one leaderboard/heatmap
   /trend layout serves all nine reports, scoped by whichever rows and
   `actionMeta` the caller passes in.
   ------------------------------------------------------------------------- */

/** CONSTANT_CASE entity value -> a readable label, e.g. 'PARTY_VEHICLE' ->
 * 'Party Vehicle'. There is no separate entity-name table any more (the old
 * `ENTITIES` constant this replaces was Incidents-specific); every action
 * already carries its `entity`, so this just formats it. */
export const entityLabel = (entityValue) =>
    entityValue
        .split('_')
        .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
        .join(' ')

/**
 * Leaderboard shaping: one record per user with a flat `created_ENTITY` /
 * `updated_ENTITY` field per entity (the record type an action operates on --
 * see `entity` on each action in the registry), sorted by total effort
 * descending.
 *
 * `actionMeta` is a lookup, not a list, like every other function in this
 * file, so there is no separate entity enumeration to keep in sync with the
 * registry -- the entity legend is built up from the rows actually visited.
 *
 * `entityColor` is a second lookup, entity -> colour (see
 * `entityColorFor` in `reportRegistry.js`), kept separate from `actionMeta`
 * because it answers a different question: an action's own `.color`
 * distinguishes created vs updated *within* one entity, but the leaderboard
 * stacks by entity, so every action on the same entity has to render in the
 * same colour here regardless of which one a row happens to be.
 *
 * `groupKeyFn`/`groupKey` default to `row.userName` -- unchanged behaviour --
 * and may instead be a list-valued key (e.g. `row.scopeNames`, for a By Board
 * leaderboard), in which case a row's count is fanned out via `groupValues`
 * to every group it names, the same convention `buildActionMix` uses.
 */
export const buildLeaderboard = (
    rows,
    actionMeta,
    entityColor,
    { groupKeyFn = (row) => row.userName, groupKey = 'userName' } = {}
) => {
    const byGroup = {}
    const entityInfo = {}

    rows.forEach((row) => {
        const meta = actionMeta(row.action)
        if (!meta) return
        const field = `${meta.mode}_${meta.entity}`
        const count = rowCount(row, actionMeta)

        groupValues(row, groupKeyFn).forEach((key) => {
            if (!byGroup[key]) byGroup[key] = { [groupKey]: key, total: 0 }
            byGroup[key][field] = (byGroup[key][field] || 0) + count
            byGroup[key].total += count
        })

        if (!entityInfo[meta.entity]) {
            entityInfo[meta.entity] = { label: entityLabel(meta.entity), color: entityColor(meta.entity) }
        }
    })

    const data = Object.values(byGroup).sort((a, b) => b.total - a.total)
    const series = Object.keys(entityInfo).map((entityValue) => ({
        key: entityValue,
        label: entityInfo[entityValue].label,
        color: entityInfo[entityValue].color,
    }))

    return { data, series }
}

/** DataTable rows for the leaderboard, split into created/updated columns. */
export const buildLeaderboardTable = (data, series, groupKey = 'userName') => {
    const columns = series.flatMap((entity) => [
        { key: `created_${entity.key}`, label: `${entity.label} created`, color: entity.color },
        { key: `updated_${entity.key}`, label: `${entity.label} updated`, color: entity.color },
    ])

    const rows = data.map((record) => ({
        name: record[groupKey],
        total: record.total || 0,
        values: columns.reduce((acc, column) => ({ ...acc, [column.key]: record[column.key] || 0 }), {}),
    }))

    return { rows, columns }
}

/**
 * The richer, people-focused KPI set the old Incidents Users tab showed:
 * total actions, active users, the busiest day, and who did the most. Kept
 * separate from `buildKpis` above rather than folded into it -- the two
 * answer different questions ("how much module activity happened" vs "who
 * did it"), and nine reports' module view depends on `buildKpis`'s current
 * shape, which this leaves untouched.
 */
export const buildUserKpis = (rows, startDate, endDate, actionMeta, granularity = 'day') => {
    const total = rows.reduce((sum, row) => sum + rowCount(row, actionMeta), 0)
    const byUser = totalsBy(rows, (row) => row.userName, actionMeta)
    const byPeriod = totalsBy(rows, (row) => periodKeyFor(row.eventDate, granularity), actionMeta)
    const periods = startDate && endDate ? periodsBetween(startDate, endDate, granularity) : []
    // Prefer the label `periodsBetween` already computed for the winning bucket
    // (correct even for a clamped edge bucket); fall back to formatting the key
    // directly only if the bucket somehow fell outside `periods` (e.g. a row
    // dated outside `[startDate, endDate]`).
    const labelForPeriod = (key) => {
        const bucket = periods.find((period) => period.key === key)
        if (bucket) return bucket.label
        return granularity === 'month' ? moment(key, 'YYYY-MM').format('MMM YYYY') : moment(key).format('MMM D')
    }

    // "Most active user" is scoped to a separate totals set that excludes
    // every System-role row outright, not just null-userId ("No Actor")
    // ones: unlike `buildKpis.activeUsers` above, which counts whatever the
    // Role filter admits with no special-case, this figure names a *person*
    // -- neither a "No Actor" row nor a service account such as
    // `cloud_functions` (a real, set `userId`, but role 'system') may ever
    // win it, even if the System role is selected and it genuinely
    // out-produced everyone else. `row.role` is what decides this, not
    // whether `userId` is null -- the `userId !== null` clause stays
    // alongside it only so rows built without a `role` field (older unit
    // test fixtures) still filter correctly. Kept separate from `byUser` so
    // `activeUsers` below still reflects everyone the filters admit, System
    // included -- a different question ("how many distinct actors" vs "who
    // is the top person").
    const humanByUser = totalsBy(
        rows.filter((row) => row.role !== 'system' && row.userId !== null),
        (row) => row.userName,
        actionMeta
    )
    const topUser = rankKeys(humanByUser)[0]
    const busiestPeriod = rankKeys(byPeriod)[0]

    return {
        totalSpark: periods.map((period) => byPeriod[period.key] || 0),
        totalActions: total,
        activeUsers: Object.keys(byUser).length,
        busiestPeriod: busiestPeriod ? labelForPeriod(busiestPeriod) : '--',
        busiestPeriodCount: busiestPeriod ? byPeriod[busiestPeriod] : 0,
        topUser: topUser || '--',
        topUserCount: topUser ? humanByUser[topUser] : 0,
    }
}

/**
 * One line per group (board) over time, capped at `limit` series by total
 * volume with the remainder folded into a single `OTHER_KEY` series. Capped
 * because the palette has eight slots and a line chart with more series than
 * colours is unreadable; `limit` defaults to seven so Other always has its
 * own distinct grey.
 */
export const buildScopeTrend = (
    rows,
    startDate,
    endDate,
    actionMeta,
    { groupKeyFn = (row) => row.userName, limit = 7, granularity = 'day' } = {}
) => {
    const periods = periodsBetween(startDate, endDate, granularity)
    const groupTotals = totalsByMulti(rows, (row) => groupValues(row, groupKeyFn), actionMeta)
    const ranked = rankKeys(groupTotals)
    const kept = ranked.slice(0, limit)
    const hasOther = ranked.length > limit
    const keyFor = (name) => (kept.includes(name) ? name : OTHER_KEY)

    const totals = {}
    rows.forEach((row) => {
        const count = rowCount(row, actionMeta)
        const periodKey = periodKeyFor(row.eventDate, granularity)
        groupValues(row, groupKeyFn).forEach((name) => {
            const key = `${periodKey}|${keyFor(name)}`
            totals[key] = (totals[key] || 0) + count
        })
    })

    const seriesKeys = hasOther ? [...kept, OTHER_KEY] : kept
    const series = seriesKeys.map((key, index) => ({
        key,
        label: key === OTHER_KEY ? 'Other' : key,
        color: key === OTHER_KEY ? OTHER_COLOR : PALETTE[index % PALETTE.length],
    }))
    const data = periods.map(({ key, label }) => {
        const record = { period: key, label, day: key }
        seriesKeys.forEach((seriesKey) => { record[seriesKey] = totals[`${key}|${seriesKey}`] || 0 })
        return record
    })
    return { data, series }
}

/** Distinct groups with activity, and the busiest one. */
export const buildScopeKpis = (rows, actionMeta, groupKeyFn) => {
    const totals = totalsByMulti(rows, (row) => groupValues(row, groupKeyFn), actionMeta)
    const top = rankKeys(totals)[0]
    return {
        activeCount: Object.keys(totals).length,
        topName: top || '--',
        topCount: top ? totals[top] : 0,
    }
}
